import _ from 'lodash';

import {fromBytes, parseBytes, toBytes} from 'utils';
import {
    ComputeConfig,
    ComputeDisk,
    ComputeOsProduct,
    ComputePlatform,
} from 'components/calculator/models';
import {ComputeFormFieldsValues} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import {I18nFunction} from 'i18n';

export function getDefaulPlatformProps(config: ComputeConfig, i18n: I18nFunction) {
    const {platforms, osProducts} = config;
    const initialValues: Partial<ComputeFormFieldsValues> = {traffic: 0};

    const osProductOptions = getOSProductOptions(osProducts);
    initialValues.osProduct = osProductOptions[0]?.value || '';

    initialValues.platform = platforms.find((platform) => platform.isDefault)?.id || '';

    const coreFractionOptions = getCoreFractionOptions(platforms, initialValues.platform);
    initialValues.coreFraction = Number(coreFractionOptions[0].value);

    const coresProps = getCoresProps(config, {
        platform: initialValues.platform,
        osProduct: initialValues.osProduct,
    });
    initialValues.cores = coresProps.minValue || 0;

    const memoryProps = getMemoryProps(
        config,
        {
            platform: initialValues.platform,
            osProduct: initialValues.osProduct,
            coreFraction: initialValues.coreFraction,
            cores: initialValues.cores,
        },
        i18n,
    );
    initialValues.memory = memoryProps.minValue;

    const diskSizeProps = getDiskSizeProps(
        {
            osProducts,
            osProduct: initialValues.osProduct,
            isBoot: true,
        },
        i18n,
    );

    return {
        ...initialValues,
        disks: [{type: 'network-hdd', size: diskSizeProps.minValue}],
    } as ComputeFormFieldsValues;
}

export function getInitialComputeValues(config: ComputeConfig, i18n: I18nFunction) {
    return {
        ...getDefaulPlatformProps(config, i18n),
        preemptible: false,
        network: false,
    };
}

export function getInitialDiskValues(): ComputeDisk {
    return {type: 'network-hdd', size: toBytes(20, 'gb')};
}

// Props

export function getCoresProps(
    config: ComputeConfig,
    values: {platform: string; osProduct?: string},
) {
    const {platforms, osProducts} = config;
    const {platform, osProduct} = values;
    const platformData = platforms.find(({id}) => id === platform);

    if (!platformData) {
        return {};
    }

    const allCoresVariants = getAllCoresVariants(platformData).map((val) => Number(val));
    const resourceSpec = osProducts?.find(({id}) => id === osProduct)?.resourceSpec;

    const min = resourceSpec
        ? Math.max(allCoresVariants[0], Number(resourceSpec.cores))
        : allCoresVariants[0];
    const max = allCoresVariants[allCoresVariants.length - 1];

    return {
        minValue: min,
        maxValue: max,
        availableValues: allCoresVariants,
        infoPointsCount: 2,
    };
}

export function getMemoryProps(
    config: ComputeConfig,
    values: Pick<ComputeFormFieldsValues, 'platform' | 'coreFraction' | 'cores'> &
        Partial<Pick<ComputeFormFieldsValues, 'osProduct'>>,
    i18n: I18nFunction,
) {
    const {osProducts, platforms} = config;
    const {osProduct, platform, coreFraction, cores} = values;
    const platformData = platforms.find(({id}) => id === platform);

    if (!platformData) {
        return {};
    }

    const platformMemoryVariants = getPlatformMemoryVariants(platformData, coreFraction, cores);
    const resourceSpec = osProducts?.find(({id}) => id === osProduct)?.resourceSpec;

    const min = resourceSpec
        ? Math.max(platformMemoryVariants[0], Number(resourceSpec.memory))
        : platformMemoryVariants[0];
    const max = platformMemoryVariants[platformMemoryVariants.length - 1];

    return {
        minValue: min,
        maxValue: max,
        availableValues: platformMemoryVariants,
        infoPointsCount: 2,
        format: (value?: number) =>
            value ? `${fromBytes(value, 'gb')} ${i18n('common', 'label_unit-gigabyte')}` : '',
        parse: (str: string) => parseBytes(str, 'gb'),
    };
}

interface GetDiskSizeOptions {
    osProducts?: ComputeOsProduct[];
    osProduct?: string;
    isBoot?: boolean;
}

export function getDiskSizeProps(
    {osProducts, osProduct, isBoot}: GetDiskSizeOptions,
    i18n: I18nFunction,
) {
    const resourceSpec = osProducts?.find(({id}) => id === osProduct)?.resourceSpec;
    const minDiskSize = resourceSpec?.diskSize || toBytes(64, 'gb');
    const minDiskSizeGB = isBoot ? Math.ceil(fromBytes(minDiskSize, 'gb')) : 1;

    return {
        minValue: toBytes(minDiskSizeGB, 'gb'),
        maxValue: toBytes(4, 'tb'),
        step: toBytes(10, 'gb'),
        format: (value?: number) =>
            value ? `${fromBytes(value, 'gb')} ${i18n('common', 'label_unit-gigabyte')}` : '',
        parse: (str: string) => parseBytes(str, 'gb'),
        infoPointsCount: 2,
    };
}

// Options

export function getOSProductOptions(osProducts?: ComputeOsProduct[]) {
    return (osProducts || []).map(({name, id}) => ({
        title: name,
        value: id,
    }));
}

export function getCoreFractionOptions(platforms: ComputePlatform[], platform: string) {
    const platformData = platforms.find(({id}) => id === platform);

    if (!platformData) {
        return [];
    }

    return platformData.allowedConfigurations
        .sort((keyA, keyB) => Number(keyA.coreFraction) - Number(keyB.coreFraction))
        .map(({coreFraction}) => ({
            content: coreFraction + '%',
            value: String(coreFraction),
        }));
}

export function getGpuCoresOptions(platforms: ComputePlatform[], platform: string) {
    const platformData = platforms.find(({id}) => id === platform);
    const allowedGpuConfigurations = platformData?.allowedGpuConfigurations;

    if (!allowedGpuConfigurations) {
        return [];
    }

    return allowedGpuConfigurations
        .sort((keyA, keyB) => Number(keyA.gpus) - Number(keyB.gpus))
        .map(({gpus}) => ({
            content: String(gpus),
            value: String(gpus),
        }));
}

// Variants

export function getPlatformCoresVariants(platformData: ComputePlatform, coreFraction: number) {
    const allowedConfiguration = findAllowedConfiguration(platformData, coreFraction);
    const coreFractionCores = allowedConfiguration?.cores;

    if (!coreFractionCores) {
        return [];
    }

    return coreFractionCores.reduce(
        (acc, {cores}) => [...acc, ...cores.map(Number)],
        [] as number[],
    );
}

export function getAllCoresVariants(platformData: ComputePlatform) {
    const allCoresVariants = platformData.allowedConfigurations.reduce((acc, {coreFraction}) => {
        const coresVariants = getPlatformCoresVariants(platformData, coreFraction);
        return acc.concat(coresVariants);
    }, [] as number[]);

    return uniqSortValues(allCoresVariants);
}

export function getPlatformMemoryVariants(
    platformData: ComputePlatform,
    coreFraction: number,
    cores: number,
) {
    const allowedConfiguration = findAllowedConfiguration(platformData, coreFraction);

    if (!allowedConfiguration) {
        return [];
    }

    const coreFractionMemoryPerCore = allowedConfiguration.memoryPerCore;
    const maxMemory = allowedConfiguration.maxMemory;

    return coreFractionMemoryPerCore.map((memoryPerCore) => {
        if (!maxMemory || Number(maxMemory) === 0) {
            return cores * memoryPerCore;
        }
        return Math.min(cores * memoryPerCore, maxMemory);
    });
}

// Other

export function findClosestValue(array: number[], goal: number) {
    return array.reduce((prev, curr) => {
        return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
    });
}

function findAllowedConfiguration(platformData: ComputePlatform, coreFraction: number) {
    return platformData.allowedConfigurations.find(
        (allowedConfiguration) =>
            Number(allowedConfiguration.coreFraction) === Number(coreFraction),
    );
}

function uniqSortValues(array: number[]) {
    const sortedArray = array.slice().sort((valueA, valueB) => valueA - valueB);
    return _.sortedUniq(sortedArray);
}
